'use client';
import React, { useState, useEffect } from 'react';
import ProfilHeader from './ProfilHeader';
import ProfilTabs from './ProfilTabs';
import MyReviewsGrid from './MyReviewsGrid';
import SavedPlacesGrid from './SavedPlacesGrid';
import ProfilSettings from './ProfilSettings';
import AuthGateModal from '@/components/AuthGateModal';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';

type TabKey = 'reviews' | 'saved' | 'settings';

interface UserProfile {
  id: string;
  username: string;
  handle: string;
  bio: string;
  avatar: string;
  isVerified: boolean;
  placesVisited: number;
  reviewsCount: number;
  savedCount: number;
  joinedDate: string;
}

export default function ProfilClient() {
  const { user, loading: authLoading } = useAuth();
  const supabase = createClient();
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<TabKey>('reviews');
  const [authOpen, setAuthOpen] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setProfileLoading(false);
      return;
    }

    const fetchProfile = async () => {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        console.log('Profile fetch error:', error.message);
        setProfileLoading(false);
        return;
      }

      const joinedDate = data.created_at
        ? new Date(data.created_at).toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })
        : 'Baru bergabung';

      setProfile({
        id: data.id,
        username: data.full_name || data.username || 'Nongki User',
        handle: `@${data.username || 'user'}`,
        bio: data.bio || 'Coffee lover ☕ | Malang explorer 🗺️',
        avatar: data.avatar_url || '',
        isVerified: data.is_verified_reviewer || false,
        placesVisited: data.review_count || 0,
        reviewsCount: data.review_count || 0,
        savedCount: 0,
        joinedDate,
      });

      // Get saved count
      const { count } = await supabase
        .from('saved_places')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      setProfile((prev) => prev ? { ...prev, savedCount: count || 0 } : prev);
      setProfileLoading(false);
    };

    fetchProfile();
  }, [user, authLoading]);

  if (authLoading || profileLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 size={28} className="animate-spin text-accent" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <div className="text-5xl mb-4">👤</div>
          <h2 className="text-xl font-800 text-primary mb-2">Login untuk lihat profil</h2>
          <p className="text-sm text-muted-foreground mb-6">Buat akun untuk simpan tempat favorit, tulis review, dan ikut Nongkrong Bareng!</p>
          <button onClick={() => router.push('/sign-up-login-screen')} className="btn-primary w-full">Masuk / Daftar</button>
        </div>
        <AuthGateModal isOpen={authOpen} onClose={() => setAuthOpen(false)} action="melihat profil" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Gagal memuat profil</p>
      </div>
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 pt-6 pb-16">
      <ProfilHeader user={profile} />
      <ProfilTabs activeTab={activeTab} onChange={setActiveTab} reviewCount={profile.reviewsCount} savedCount={profile.savedCount} />

      <div className="mt-6">
        {activeTab === 'reviews' && <MyReviewsGrid userId={user.id} />}
        {activeTab === 'saved' && <SavedPlacesGrid userId={user.id} />}
        {activeTab === 'settings' && <ProfilSettings user={profile} />}
      </div>
    </div>
  );
}